import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { getClientEnv } from '@lykbl/config';

let cachedClient: SupabaseClient | null = null;

// Local development defaults only - no production fallbacks for security
const DEFAULT_LOCAL_SUPABASE_URL = 'http://localhost:54321';
const DEFAULT_LOCAL_ANON_KEY = 'public-anon-key';

/**
 * Check if we're running in local development environment
 */
const inLocalRuntime = (): boolean => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    return ['localhost', '127.0.0.1', '0.0.0.0', '::1'].includes(hostname);
  }

  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) {
    return true;
  }

  if (typeof process !== 'undefined' && process.env) {
    const { NODE_ENV, VITE_ENVIRONMENT } = process.env as Record<string, string | undefined>;
    if ((NODE_ENV && NODE_ENV !== 'production') || (VITE_ENVIRONMENT && VITE_ENVIRONMENT !== 'production')) {
      return true;
    }
  }

  return false;
};

/**
 * Resolve Supabase URL with local development fallback
 */
const resolveSupabaseUrl = (): string => {
  try {
    const { SUPABASE_URL } = getClientEnv();
    if (SUPABASE_URL && SUPABASE_URL.trim() && !SUPABASE_URL.includes('your-')) {
      return SUPABASE_URL;
    }
  } catch (err) {
    // getClientEnv() throws if env vars are missing
  }

  if (inLocalRuntime()) {
    console.warn('[marketing:supabase] Falling back to local Supabase URL because VITE_SUPABASE_URL is not set.');
    return DEFAULT_LOCAL_SUPABASE_URL;
  }

  // FAIL CLOSED: Do not use hardcoded production fallbacks
  throw new Error(
    'VITE_SUPABASE_URL environment variable is required for production builds.\n' +
    'Configure this in your Cloudflare Pages environment settings.'
  );
};

/**
 * Resolve Supabase anon key with local development fallback
 */
const resolveSupabaseAnonKey = (): string => {
  try {
    const { SUPABASE_ANON_KEY } = getClientEnv();
    if (SUPABASE_ANON_KEY && SUPABASE_ANON_KEY.trim() && SUPABASE_ANON_KEY !== 'public-anon-key' && !SUPABASE_ANON_KEY.includes('your-')) {
      return SUPABASE_ANON_KEY;
    }
  } catch (err) {
    // getClientEnv() throws if env vars are missing
  }

  if (inLocalRuntime()) {
    console.warn('[marketing:supabase] Falling back to local Supabase anon key because VITE_SUPABASE_ANON_KEY is not set.');
    return DEFAULT_LOCAL_ANON_KEY;
  }

  // FAIL CLOSED: Do not use hardcoded production fallbacks
  throw new Error(
    'VITE_SUPABASE_ANON_KEY environment variable is required for production builds.\n' +
    'Configure this in your Cloudflare Pages environment settings.'
  );
};

/**
 * Shared Supabase browser client for the Marketing app.
 * Provides local development fallbacks for easier local dev setup.
 */
export const getSupabaseClient = (): SupabaseClient => {
  if (cachedClient) return cachedClient;

  const supabaseUrl = resolveSupabaseUrl();
  const supabaseAnonKey = resolveSupabaseAnonKey();

  cachedClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
    },
  });

  return cachedClient;
};

// Lazy initialization - only create client when accessed, not at module load
let _supabase: SupabaseClient | null = null;
let _initError: Error | null = null;

const getSupabaseClientLazy = (): SupabaseClient => {
  if (_supabase) return _supabase;
  if (_initError) {
    if (!inLocalRuntime()) {
      throw _initError;
    }
  }

  try {
    _supabase = getSupabaseClient();
    return _supabase;
  } catch (err) {
    _initError = err instanceof Error ? err : new Error(String(err));
    
    if (inLocalRuntime()) {
      console.error('[marketing:supabase] Failed to initialize Supabase client:', err);
      console.warn('[marketing:supabase] App will continue in public mode. Auth features will not work.');
      
      _supabase = createClient(
        DEFAULT_LOCAL_SUPABASE_URL,
        DEFAULT_LOCAL_ANON_KEY,
        {
          auth: {
            persistSession: false,
            autoRefreshToken: false,
            detectSessionInUrl: false,
          },
        }
      ) as SupabaseClient;
      return _supabase;
    }
    
    throw _initError;
  }
};

// Export supabase as a lazy getter
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getSupabaseClientLazy();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  },
}) as SupabaseClient;

